/**
 * Congestion handling for horizontal price bands.
 *
 * A retrograde study draws one band per cycle and they rarely collide. Opening bands up to every
 * area-highlight study changes that: an aspect study can produce dozens of date pairs, and their
 * bands land on top of each other in both axes. Stacked translucent fills compound into a wash
 * that hides the candles underneath, and every band's edge looks like every other band's edge.
 *
 * Bands that overlap another band are therefore drawn with a dotted border instead of a solid one.
 * Dotting only the crowded ones is the point: if every band were dotted the cue would carry no
 * information, and dotting is legible precisely because it marks the boundaries that are hard to
 * separate. The fill is left alone — dimming it further would make crowded regions vanish rather
 * than resolve.
 */

/** Border style for a band that overlaps nothing. */
export const BORDER_SOLID = 'solid';
/** Border style for a band sharing space with another. */
export const BORDER_CONGESTED = 'dotted';

/** Closed-interval overlap: touching at a single value counts. Used for the price axis. */
const overlaps1d = (aMin, aMax, bMin, bMax) => aMin <= bMax && bMin <= aMax;

/**
 * Open-interval overlap: touching at a single value does NOT count.
 *
 * <p>Used for the time axis, and it is the whole reason retrograde bands kept losing their edges.
 * The bands TILE — each runs from the end of one retrograde to the start of the next — so
 * consecutive bands share exactly one bar index by construction. Under the closed test every
 * adjacent pair was "congested", which dotted and faded almost every edge on the chart and made
 * the bands read as having no boundary at all. Sharing one bar column is abutment, not crowding.
 */
const overlapsMoreThanAPoint = (aMin, aMax, bMin, bMax) => aMin < bMax && bMin < aMax;

/**
 * Do two bands share screen space?
 *
 * Both axes must overlap. Bands that share an x-range at different price levels sit above one
 * another and read perfectly well; only when the price ranges also intersect do the edges become
 * hard to tell apart.
 */
export function bandsOverlap(a, b) {
  if (!a || !b) return false;
  const ax = [Math.min(a.x1, a.x2), Math.max(a.x1, a.x2)];
  const bx = [Math.min(b.x1, b.x2), Math.max(b.x1, b.x2)];
  const ay = [Math.min(a.price1, a.price2), Math.max(a.price1, a.price2)];
  const by = [Math.min(b.price1, b.price2), Math.max(b.price1, b.price2)];
  return overlapsMoreThanAPoint(ax[0], ax[1], bx[0], bx[1]) && overlaps1d(ay[0], ay[1], by[0], by[1]);
}

/**
 * Annotates each band with whether it is congested, and the border style to draw it with.
 *
 * @param {Array<{x1:number,x2:number,price1:number,price2:number}>} bands
 * @returns {Array} the same bands, each with `isCongested` and `borderType` added
 */
export function annotateBandCongestion(bands) {
  if (!Array.isArray(bands)) return [];
  return bands.map((band, i) => {
    const congested = bands.some((other, j) => j !== i && bandsOverlap(band, other));
    return {
      ...band,
      isCongested: congested,
      borderType: congested ? BORDER_CONGESTED : BORDER_SOLID,
    };
  });
}

/**
 * Lightens a band's stroke so a crowded cluster of edges does not read as a solid mass.
 *
 * <p>The factor is 0.8, not 0.5. At a half the amber stroke fell to alpha 0.4 and the edge stopped
 * being findable at all — which defeats the purpose, since the reason for marking a crowded band
 * is to keep its boundary readable among the others. The DOTTING is what distinguishes a congested
 * band; the fade only softens the stack, and it must not do so to the point of erasure.
 *
 * Only touches `rgba(...)` values, which is what the band palette uses; anything else is returned
 * unchanged rather than guessed at.
 */
export function lightenStroke(rgba, factor = 0.8) {
  if (typeof rgba !== 'string') return rgba;
  const m = rgba.match(/^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/);
  if (!m) return rgba;
  const alpha = Math.max(0, Math.min(1, parseFloat(m[4]) * factor));
  return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
}

export default annotateBandCongestion;
